'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useViewportHeight } from '@shared/utils/hooks/use-viewport-height';
import { useSubNavigationContext } from '../../../providers/sub-navigation-context';
const FOCUS_LEVEL = 200;
const FOCUS_AREA_HEIGHT = 1; // using 1px height to avoid having two elements intersecting simultaneously

const getObserverOptions = (viewPortHeight: number) => ({
  rootMargin: `-${FOCUS_LEVEL}px 0px -${viewPortHeight - FOCUS_LEVEL - FOCUS_AREA_HEIGHT}px 0px`
});
type VisibleElement = null | string;
type SetPriorityElement = (elementId: string, expirationTime?: number) => void;
export const useVisibleElement = t0 => {
  const $ = _c(19);
  const {
    disabled: t1,
    elementIds
  } = t0;
  const disabled = t1 === undefined ? false : t1;
  const viewPortHeight = useViewportHeight();
  const {
    elements,
    registerLinks
  } = useSubNavigationContext();
  const [visibleElement, setVisibleElement] = useState(null);
  const priorityElement = useRef(null);
  const visibleNonPriorityElement = useRef(null);
  let t2;
  let t3;
  if ($[0] !== disabled || $[1] !== elementIds || $[2] !== registerLinks) {
    t2 = () => {
      if (!disabled) {
        registerLinks(elementIds);
      }
    };
    t3 = [disabled, elementIds, registerLinks];
    $[0] = disabled;
    $[1] = elementIds;
    $[2] = registerLinks;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== disabled || $[6] !== elements || $[7] !== viewPortHeight) {
    t4 = () => {
      if (!viewPortHeight || !elements || disabled) {
        return _temp;
      }
      const onIntersect = entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visibleNonPriorityElement.current = entry.target.id;
            if (!priorityElement.current) {
              setVisibleElement(entry.target.id);
            }
            continue;
          }
          if (!priorityElement) {
            setVisibleElement(visibleNonPriorityElement.current);
          }
        }
      };
      const options = getObserverOptions(viewPortHeight);
      const observer = new IntersectionObserver(onIntersect, options);
      for (const element of elements) {
        observer.observe(element);
      }
      return () => observer.disconnect();
    };
    $[5] = disabled;
    $[6] = elements;
    $[7] = viewPortHeight;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== disabled || $[10] !== elementIds || $[11] !== elements || $[12] !== viewPortHeight) {
    t5 = [disabled, elementIds, elements, viewPortHeight];
    $[9] = disabled;
    $[10] = elementIds;
    $[11] = elements;
    $[12] = viewPortHeight;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== elements) {
    t6 = (elementId, t7) => {
      const expirationTime = t7 === undefined ? 1000 : t7;
      if (!elements) {
        return;
      }
      const foundElement = elements.find(element_0 => element_0.id === elementId);
      if (!foundElement) {
        return;
      }
      priorityElement.current = {
        hasEntered: false
      };
      const onPriorityElementIntersect = entries_0 => {
        for (const entry_0 of entries_0) {
          if (entry_0.isIntersecting) {
            priorityElement.current = {
              hasEntered: true
            };
            setVisibleElement(entry_0.target.id);
            continue;
          }
          if (!entry_0.isIntersecting && priorityElement.current?.hasEntered) {
            priorityElement.current = null;
            setVisibleElement(visibleNonPriorityElement.current);
          }
        }
      };
      const observer_0 = new IntersectionObserver(onPriorityElementIntersect);
      observer_0.observe(foundElement);
      setTimeout(() => {
        observer_0.disconnect();
        priorityElement.current = null;
      }, expirationTime);
    };
    $[14] = elements;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const setPriorityElement = t6;
  let t7;
  if ($[16] !== setPriorityElement || $[17] !== visibleElement) {
    t7 = [visibleElement, setPriorityElement];
    $[16] = setPriorityElement;
    $[17] = visibleElement;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  return t7;
};
function _temp() {}